/**
 * SCHEMA DETAYLI GÖRÜNÜM — ham JSON yerine okunabilir ağaç.
 *
 * Ham JSON-LD bir SEO uzmanı için yeterli değil: hangi alan Google'a göre zorunlu,
 * hangisi önerilen, hangisi tipe ait bile değil — bunu düz metinde göremiyorsun.
 * Bu görünüm her alanı ayrı satıra açar ve yanına rozetini koyar:
 *
 *   ZORUNLU   Google zengin sonuç için şart, DOLU
 *   EKSİK     zorunlu ama sayfada yok  → zengin sonuç alınmaz
 *   ÖNERİLEN  varsa gösterim güçlenir
 *   EKSTRA    kural listesinde yok (yanlış olduğu anlamına gelmez)
 *
 * İç içe nesneler ve diziler açılıp kapanır; derin yapıda kaybolmamak için
 * her seviye girintili ve kendi tipini gösterir.
 */
import { useState } from "react";

export type TipKurali = { tip: string; ad: string; zorunlu: string[]; onerilen: string[]; not: string | null };

const ROZET = {
  zorunlu:  "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400",
  eksik:    "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400",
  onerilen: "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400",
  ekstra:   "bg-accent text-muted-foreground",
} as const;

const tipiAl = (o: unknown): string | null => {
  const t = (o as { "@type"?: unknown })?.["@type"];
  if (typeof t === "string") return t;
  if (Array.isArray(t) && typeof t[0] === "string") return t[0];
  return null;
};

/** Tek bir değer satırı — ilkel değerse yazar, nesne/diziyse açılır kapanır. */
function Satir({ ad, deger, rozet, derinlik }: { ad: string; deger: unknown; rozet?: keyof typeof ROZET; derinlik: number }) {
  // İlk İKİ seviye açık gelir. Kullanıcı "gizlemene gerek yok" dedi; ama çok
  // derin/uzun dizilerde her şeyi açmak da okunmaz hale getiriyor, o yüzden
  // 3. seviyeden itibaren katlı başlar.
  const [acik, setAcik] = useState(derinlik < 2);
  const nesne = deger !== null && typeof deger === "object";
  const dizi = Array.isArray(deger);
  const altTip = nesne && !dizi ? tipiAl(deger) : null;

  const etiketi = (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-medium">{ad}</span>
      {rozet && <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${ROZET[rozet]}`}>{rozet}</span>}
      {altTip && <span className="text-[10px] text-muted-foreground">({altTip})</span>}
    </span>
  );

  if (!nesne) {
    const metin = String(deger ?? "");
    const url = /^https?:\/\//.test(metin.trim());
    // Baş/son boşluk gözle görünmüyor — burada açıkça işaretlenir.
    const bosluklu = metin !== metin.trim() && metin.trim().length > 0;
    return (
      <div className="flex items-start gap-2 py-0.5 text-[12px]" style={{ paddingLeft: derinlik * 14 }}>
        <span className="shrink-0 min-w-[130px]">{etiketi}</span>
        <span className={`break-all ${bosluklu ? "text-error-600 dark:text-error-400" : "text-muted-foreground"}`}>
          {url
            ? <a href={metin.trim()} target="_blank" rel="noreferrer" className="underline">{metin}</a>
            : metin.length > 220 ? `${metin.slice(0, 220)}…` : metin}
          {bosluklu && <span className="ml-1 text-[10px] font-semibold">← baştaki/sondaki boşluk</span>}
        </span>
      </div>
    );
  }

  const cocuklar = dizi
    ? (deger as unknown[]).map((v, i) => [String(i + 1), v] as const)
    : Object.entries(deger as Record<string, unknown>).filter(([k]) => k !== "@type");

  return (
    <div style={{ paddingLeft: derinlik * 14 }}>
      <button onClick={() => setAcik((v) => !v)} className="flex items-center gap-1.5 py-0.5 text-[12px] text-left">
        <span className="text-muted-foreground w-3 shrink-0">{acik ? "▾" : "▸"}</span>
        {etiketi}
        <span className="text-[10px] text-muted-foreground">{dizi ? `${cocuklar.length} öğe` : `${cocuklar.length} alan`}</span>
      </button>
      {acik && cocuklar.map(([k, v]) => <Satir key={k} ad={k} deger={v} derinlik={derinlik + 1} />)}
    </div>
  );
}

export function SchemaAgaci({ blok, kural }: { blok: unknown; kural?: TipKurali }) {
  const tip = tipiAl(blok) ?? "(tip yok)";
  const alanlar = Object.entries((blok ?? {}) as Record<string, unknown>).filter(([k]) => k !== "@type" && k !== "@context");
  const varOlan = new Set(alanlar.map(([k]) => k));
  // SAVUNMACI: kural nesnesi eski şekilde (yalnız tip+ad) gelebilir — sunucu
  // güncellenmeden arayüz güncellenirse böyle olur ve `.includes` çökerdi.
  // Alanların yokluğu bir gösterim eksikliğidir, sayfayı düşürmemeli.
  const zorunluListe = Array.isArray(kural?.zorunlu) ? kural!.zorunlu : [];
  const onerilenListe = Array.isArray(kural?.onerilen) ? kural!.onerilen : [];
  const kuralVar = zorunluListe.length > 0 || onerilenListe.length > 0;
  const eksikZorunlu = zorunluListe.filter((z) => !varOlan.has(z));
  const eksikOnerilen = onerilenListe.filter((o) => !varOlan.has(o));

  const rozetiBul = (ad: string): keyof typeof ROZET => {
    if (zorunluListe.includes(ad)) return "zorunlu";
    if (onerilenListe.includes(ad)) return "onerilen";
    return "ekstra";
  };

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="font-semibold text-sm">{tip}</span>
        {kural?.ad && <span className="text-[11px] text-muted-foreground">{kural.ad}</span>}
        {kuralVar && (eksikZorunlu.length === 0
          ? <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400">ZENGİN SONUCA HAZIR</span>
          : <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400">ZENGİN SONUÇ ALINMAZ</span>)}
        {!kuralVar && <span className="text-[10px] text-muted-foreground">Google kuralı tanımlı değil — yalnız sözlük denetimi</span>}
      </div>

      <div className="space-y-0.5">
        {alanlar.map(([k, v]) => <Satir key={k} ad={k} deger={v} rozet={rozetiBul(k)} derinlik={0} />)}
      </div>

      {(eksikZorunlu.length > 0 || eksikOnerilen.length > 0) && (
        <div className="mt-2 pt-2 border-t border-border space-y-1">
          {eksikZorunlu.length > 0 && (
            <div className="flex items-start gap-2 text-[12px]">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase shrink-0 ${ROZET.eksik}`}>eksik</span>
              <span className="text-muted-foreground"><b>Zorunlu:</b> {eksikZorunlu.join(", ")} — bu haliyle zengin sonuç alınmaz.</span>
            </div>
          )}
          {eksikOnerilen.length > 0 && (
            <div className="flex items-start gap-2 text-[12px]">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase shrink-0 ${ROZET.onerilen}`}>önerilen</span>
              <span className="text-muted-foreground">{eksikOnerilen.join(", ")} — eklenirse gösterim güçlenir.</span>
            </div>
          )}
          {kural?.not && <p className="text-[11px] text-muted-foreground">{kural.not}</p>}
        </div>
      )}
    </div>
  );
}

/** Ham JSON metinlerini ayrıştırıp ağaç listesine çevirir; bozuk olanı atlar. */
export function SchemaAgaclari({ hamKodlar, kurallar }: { hamKodlar: string[]; kurallar: TipKurali[] }) {
  const bloklar = hamKodlar.map((k) => { try { return JSON.parse(k) as unknown; } catch { return null; } }).filter(Boolean);
  if (bloklar.length === 0) return <p className="text-[12px] text-muted-foreground">Gösterilecek schema bulunamadı.</p>;
  return (
    <div className="space-y-2">
      {bloklar.map((b, i) => (
        <SchemaAgaci key={i} blok={b} kural={kurallar.find((k) => k.tip === tipiAl(b))} />
      ))}
    </div>
  );
}

/**
 * HAM KOD — her blok KENDİ tip başlığı altında ayrı kutuda.
 * Tek bir dev metin yerine bloklara bölmek, çok schema'lı sayfalarda hangi
 * kodun hangi tipe ait olduğunu tek bakışta gösteriyor.
 */
export function SchemaHamBloklar({ hamKodlar }: { hamKodlar: string[] }) {
  if (!hamKodlar.length) return <p className="text-[12px] text-muted-foreground">Gösterilecek schema bulunamadı.</p>;
  return (
    <div className="space-y-2">
      {hamKodlar.map((kod, i) => {
        let tipler = "(tip yok)";
        try {
          const o = JSON.parse(kod) as { "@type"?: unknown };
          const t = o["@type"];
          tipler = typeof t === "string" ? t : Array.isArray(t) ? t.filter((x) => typeof x === "string").join(", ") : "(tip yok)";
        } catch { tipler = "(geçersiz JSON)"; }
        return (
          <div key={i} className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-accent">
              <span className="text-muted-foreground text-[11px] font-mono">&lt;/&gt;</span>
              <span className="font-semibold text-[12px]">{tipler}</span>
            </div>
            <pre className="text-[11px] p-3 overflow-x-auto max-h-96">{kod}</pre>
          </div>
        );
      })}
    </div>
  );
}
